import { inject, Injectable, signal } from '@angular/core';
import { IApplication } from '../shared/models/application.interface';
import { ApplicationsApiService } from './applications.api.service';
import { finalize, tap } from 'rxjs';

import { PaginationService } from 'app/shared/components/pagination/pagination.service';
import { ResourceParameters } from 'app/shared';
import { ApplicationsLoadingEnum, IApplicationForCreation, LoadingState } from './@shared';

@Injectable({
    providedIn: 'root'
})
export class ApplicationsStateService {
    private readonly _applicationsApiService = inject(ApplicationsApiService);
    private readonly _paginationService = inject(PaginationService);

    private readonly _applications = signal<IApplication[]>([]);
    private readonly _loadingState = signal<LoadingState>({
        active: false,
        type: ApplicationsLoadingEnum.Loading
    });
    private readonly _searchQuery = signal<string>('');
    private readonly _currentParams = signal<ResourceParameters>({
        pageNumber: 1,
        pageSize: 10
    });

    readonly applications = this._applications.asReadonly();
    readonly loadingState = this._loadingState.asReadonly();
    readonly searchQuery = this._searchQuery.asReadonly();
    readonly currentParams = this._currentParams.asReadonly();

    loadApplicationsWithSearch(params: ResourceParameters, loadingType: ApplicationsLoadingEnum) {
        this._loadingState.set({ active: true, type: loadingType });
        this._currentParams.set(params);

        return this._applicationsApiService.getApplications(params).pipe(
            tap((response) => {
                this._applications.set(response.data);
                this._paginationService.setPagination(response.pagination);
            }),
            finalize(() => this._loadingState.set({ active: false, type: null }))
        );
    }

    setApplications(applications: IApplication[]) {
        this._applications.set(applications);
    }

    setLoadingState(status: boolean, loadingType: ApplicationsLoadingEnum = ApplicationsLoadingEnum.Loading, origin?: string) {
        this._loadingState.set({
            active: status,
            type: loadingType,
            origin
        });
    }

    createApplication(application: IApplicationForCreation) {
        return this._applicationsApiService.createApplication(application).pipe(
            tap((res) => {
                this.setApplications([...this._applications(), res]);
            })
        );
    }

    editApplication(id: string, application: IApplicationForCreation) {
        return this._applicationsApiService.updateApplication(id, application).pipe(
            tap((res) => {
                const applications = this._applications();
                const appIndex = applications.findIndex(a => a.id === id);
                applications[appIndex] = res;
                this.setApplications(applications);
            })
        );
    }

    removeApplication(id: string) {
        return this._applicationsApiService.deleteApplication(id).pipe(
            tap(() => {
                this.setApplications(this._applications().filter(a => a.id !== id));
            })
        );
    }
} 